"use client";

import { useGetServices } from "@/hooks/main-hooks";
import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { ServiceItem, ServiceRow } from "../services/services";
import { SkeletonRow } from "../services/skeleton";

/* ─── Main ─── */
export default function Services() {
  const t = useTranslations("Services");
  const locale = useLocale();
  const isAr = locale === "ar";
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading } = useGetServices({ page: currentPage, limit: 40 });

  const services: ServiceItem[] = data?.data || [];
  const totalPages: number = data?.paginate?.totalPages || 1;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;0,9..144,700;0,9..144,800;1,9..144,700&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600&display=swap');

        .headline {
          font-size: clamp(3rem, 6vw, 5.5rem);
        }
      `}</style>

      <section dir={isAr ? "rtl" : "ltr"} className="bg-[#F6F6FA] pb-[120px]">
        {/* ── Header ── */}
        <div className="pt-24 pb-[72px]">
          <div className="container mx-auto px-6">
            <div
              className={`flex flex-col gap-6 ${
                isAr ? "items-end" : "items-start"
              }`}
            >
              {/* eyebrow */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-2.5 bg-white border border-[#EAEAF0] rounded-full px-[18px] py-2"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#0A0A14] block shrink-0" />
                <span
                  className="text-[10px] font-medium tracking-[0.25em] uppercase text-[#0A0A14]"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  {t("ourServices")}
                </span>
              </motion.div>

              {/* headline */}
              <motion.h1
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.7,
                  delay: 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="headline font-extrabold text-[#0A0A14] leading-none tracking-[-0.04em] m-0 max-w-[900px]"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                {t("integrated")}{" "}
                <span
                  className="italic"
                  style={{
                    WebkitTextStroke: "1.5px #0A0A14",
                    color: "transparent",
                  }}
                >
                  {t("aluminum")}
                </span>
                <br />
                {t("architecture")}
              </motion.h1>

              {/* sub */}
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.18 }}
                className="text-base text-[#7A7A90] leading-[1.7] max-w-[600px] m-0"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t("specializedServices")}
              </motion.p>

              {/* sub */}
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.18 }}
                className="text-base text-[#7A7A90] leading-[1.7] max-w-[900px] m-0"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {t("advancedCapabilities")}
              </motion.p>
            </div>
          </div>
        </div>

        {/* ── Services Grid ── */}
        <div className="container mx-auto px-6">
          <div className="flex flex-col gap-6">
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonRow key={i} idx={i} />
                ))
              : services.map((service, idx) => (
                  <ServiceRow
                    key={service.id}
                    service={service}
                    idx={idx}
                    isAr={isAr}
                    onClick={() =>
                      router.push(`/${locale}/services/${service.slug}`)
                    }
                  />
                ))}
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-[72px] ltr">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`h-2.5 rounded-full border-none cursor-pointer p-0 transition-all duration-350 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    i + 1 === currentPage
                      ? "w-11 bg-[#0A0A14]"
                      : "w-2.5 bg-[#D8D8E8]"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
